import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calendar, Share2, AlertTriangle, Cloud, Clock } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Link } from "react-router-dom";

const AWSOutageArticle = () => {
  const { t } = useLanguage();

  const impactData = [
    { region: t('article.aws.chart.usEast'), affected: 85, recovered: 95 },
    { region: t('article.aws.chart.europe'), affected: 45, recovered: 98 },
    { region: t('article.aws.chart.asiaPacific'), affected: 25, recovered: 100 },
  ];

  const sectorImpact = [
    { name: t('article.aws.sectors.ecommerce'), value: 35, color: '#ef4444' },
    { name: t('article.aws.sectors.logistics'), value: 28, color: '#f97316' },
    { name: t('article.aws.sectors.manufacturing'), value: 20, color: '#eab308' },
    { name: t('article.aws.sectors.retail'), value: 17, color: '#22c55e' },
  ];

  const shareArticle = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/">
                <Button variant="outline" size="sm">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  {t('common.back')}
                </Button>
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-blue-900">{t('header.title')}</h1>
                <p className="text-blue-600">{t('header.subtitle')}</p>
              </div>
            </div>
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        <article className="max-w-4xl mx-auto">
          {/* Article Header */}
          <div className="mb-8">
            <Badge className="mb-4 bg-red-500">
              <AlertTriangle className="h-3 w-3 mr-1" />
              {t('common.highRisk')}
            </Badge>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {t('article.aws.title')}
            </h1>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center text-gray-600">
                <Calendar className="h-4 w-4 mr-2" />
                <span>{t('article.aws.date')}</span>
                <span className="mx-2">•</span>
                <span>{t('article.aws.readTime')}</span>
              </div>
              <Button onClick={shareArticle} variant="outline" size="sm">
                <Share2 className="h-4 w-4 mr-2" />
                {t('common.share')}
              </Button>
            </div>
          </div>

          {/* Featured Image */}
          <div className="mb-8">
            <div className="w-full h-96 bg-gradient-to-r from-red-100 to-orange-100 rounded-lg shadow-lg flex items-center justify-center">
              <div className="text-center">
                <Cloud className="h-24 w-24 text-red-500 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-red-800">AWS Service Disruption</h3>
                <p className="text-red-600">Global Supply Chain Impact</p>
              </div>
            </div>
            <p className="text-sm text-gray-500 mt-2 text-center">
              {t('article.aws.imageCaption')}
            </p>
          </div>

          {/* Article Content */}
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 mb-6 leading-relaxed">
              {t('article.aws.lead')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('article.aws.section1.title')}
            </h2>
            <p className="text-gray-700 mb-6">
              {t('article.aws.section1.content')}
            </p>

            {/* Impact Chart */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Cloud className="h-5 w-5 mr-2 text-red-600" />
                  {t('article.aws.chart.title')}
                </CardTitle>
                <CardDescription>{t('article.aws.chart.description')}</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={impactData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="region" />
                    <YAxis />
                    <Tooltip formatter={(value) => [`${value}%`, '']} />
                    <Bar dataKey="affected" fill="#ef4444" name={t('article.aws.chart.affected')} />
                    <Bar dataKey="recovered" fill="#10b981" name={t('article.aws.chart.recovered')} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('article.aws.section2.title')}
            </h2>
            <p className="text-gray-700 mb-6">
              {t('article.aws.section2.content')}
            </p>

            {/* Sector Impact Pie Chart */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle>{t('article.aws.sectorChart.title')}</CardTitle>
                <CardDescription>{t('article.aws.sectorChart.description')}</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={sectorImpact}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}%`}
                    >
                      {sectorImpact.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <div className="bg-red-50 p-6 rounded-lg mb-6">
              <h3 className="text-lg font-semibold text-red-900 mb-3">
                {t('article.aws.keyStats.title')}
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <div className="text-2xl font-bold text-red-800">4.2h</div>
                  <div className="text-sm text-red-600">{t('article.aws.keyStats.duration')}</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-red-800">$2.3B</div>
                  <div className="text-sm text-red-600">{t('article.aws.keyStats.estimatedLoss')}</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-red-800">15,000+</div>
                  <div className="text-sm text-red-600">{t('article.aws.keyStats.affectedServices')}</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-red-800">98%</div>
                  <div className="text-sm text-red-600">{t('article.aws.keyStats.recoveryRate')}</div>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('article.aws.section3.title')}
            </h2>
            <p className="text-gray-700 mb-6">
              {t('article.aws.section3.content')}
            </p>

            <div className="bg-yellow-50 p-6 rounded-lg mb-6">
              <h3 className="text-lg font-semibold text-yellow-900 mb-3">
                {t('article.aws.timeline.title')}
              </h3>
              <div className="space-y-4">
                <div className="flex items-center">
                  <Clock className="h-4 w-4 text-yellow-600 mr-3" />
                  <div>
                    <div className="font-semibold">09:15 UTC</div>
                    <div className="text-sm text-gray-600">{t('article.aws.timeline.start')}</div>
                  </div>
                </div>
                <div className="flex items-center">
                  <Clock className="h-4 w-4 text-orange-600 mr-3" />
                  <div>
                    <div className="font-semibold">10:30 UTC</div>
                    <div className="text-sm text-gray-600">{t('article.aws.timeline.peak')}</div>
                  </div>
                </div>
                <div className="flex items-center">
                  <Clock className="h-4 w-4 text-green-600 mr-3" />
                  <div>
                    <div className="font-semibold">13:30 UTC</div>
                    <div className="text-sm text-gray-600">{t('article.aws.timeline.resolution')}</div>
                  </div>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('article.aws.section4.title')}
            </h2>
            <p className="text-gray-700 mb-6">
              {t('article.aws.section4.content')}
            </p>

            {/* Source Link */}
            <div className="bg-gray-50 p-6 rounded-lg mb-8 border-l-4 border-l-orange-500">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                {t('article.source.title')}
              </h3>
              <p className="text-gray-600 mb-3">{t('article.source.description')}</p>
              <a 
                href="https://www.reuters.com/technology/aws-outage-disrupts-services-across-multiple-regions-2024-10-25/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
              >
                <span className="mr-2">☁️</span>
                {t('article.source.readOriginal')}
              </a>
              <p className="text-sm text-gray-500 mt-2">
                {t('article.source.publisher')}: Reuters Technology
              </p>
            </div>

            <div className="bg-blue-50 p-6 rounded-lg mb-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-3">
                {t('article.aws.recommendations.title')}
              </h3>
              <ul className="space-y-2 text-blue-800">
                <li>• {t('article.aws.recommendations.multiCloud')}</li>
                <li>• {t('article.aws.recommendations.backupSystems')}</li>
                <li>• {t('article.aws.recommendations.monitoring')}</li>
                <li>• {t('article.aws.recommendations.contingency')}</li>
              </ul>
            </div>
          </div>

          {/* Related Articles */}
          <div className="mt-12 pt-8 border-t">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              {t('article.relatedArticles')}
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <Link to="/article/port-strikes">
                <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                  <CardHeader>
                    <CardTitle className="text-lg">{t('risk.strikes.title')}</CardTitle>
                    <CardDescription>{t('industry.freight.date')}</CardDescription>
                  </CardHeader>
                </Card>
              </Link>
              <Link to="/article/freight-rates">
                <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                  <CardHeader>
                    <CardTitle className="text-lg">{t('industry.freight.title')}</CardTitle>
                    <CardDescription>{t('industry.freight.date')}</CardDescription>
                  </CardHeader>
                </Card>
              </Link>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
};

export default AWSOutageArticle;